/**
 * IdeaVoteBar Component
 * 
 * Prominent voting UI for idea detail view.
 * Shows vote counts and allows voting with feedback.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, text, space, radius, icon, shadow } from '../../../shared/tokens/mobile';
import { VoteType } from '../../../shared/types/idea';

interface IdeaVoteBarProps {
  upvotes: number;
  downvotes: number;
  score: number;
  userVote: VoteType | null;
  onVote: (voteType: VoteType | null) => void;
  voting?: boolean;
}

export function IdeaVoteBar({ upvotes, downvotes, score, userVote, onVote, voting }: IdeaVoteBarProps) {
  const handleUpvote = () => {
    if (voting) return;
    onVote(userVote === 'up' ? null : 'up');
  };

  const handleDownvote = () => {
    if (voting) return;
    onVote(userVote === 'down' ? null : 'down');
  };

  return (
    <View style={styles.container}>
      {/* Upvote Button */}
      <Pressable
        onPress={handleUpvote}
        disabled={voting}
        style={({ pressed }) => [
          styles.voteButton,
          userVote === 'up' && styles.voteButtonActive,
          userVote === 'up' && styles.upvoteActive,
          pressed && styles.voteButtonPressed,
        ]}
      >
        {voting ? (
          <ActivityIndicator size="small" color={userVote === 'up' ? color.surface : color.success} />
        ) : (
          <>
            <Ionicons
              name={userVote === 'up' ? 'arrow-up' : 'arrow-up-outline'}
              size={icon.lg}
              color={userVote === 'up' ? color.surface : color.success}
            />
            <Text style={[styles.voteCount, userVote === 'up' && styles.voteCountActive]}>
              {upvotes}
            </Text>
          </>
        )}
      </Pressable>

      {/* Score Display */}
      <View style={styles.scoreContainer}>
        <Text style={[
          styles.score,
          score > 0 && styles.scorePositive,
          score < 0 && styles.scoreNegative,
        ]}>
          {score > 0 ? '+' : ''}{score}
        </Text>
        <Text style={styles.scoreLabel}>score</Text>
      </View>

      {/* Downvote Button */}
      <Pressable
        onPress={handleDownvote}
        disabled={voting}
        style={({ pressed }) => [
          styles.voteButton,
          userVote === 'down' && styles.voteButtonActive,
          userVote === 'down' && styles.downvoteActive,
          pressed && styles.voteButtonPressed,
        ]}
      >
        {voting ? (
          <ActivityIndicator size="small" color={userVote === 'down' ? color.surface : color.error} />
        ) : (
          <>
            <Ionicons
              name={userVote === 'down' ? 'arrow-down' : 'arrow-down-outline'}
              size={icon.lg}
              color={userVote === 'down' ? color.surface : color.error}
            />
            <Text style={[styles.voteCount, userVote === 'down' && styles.voteCountActive]}>
              {downvotes}
            </Text>
          </>
        )}
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: space.lg,
    paddingVertical: space.md,
    paddingHorizontal: space.lg,
    backgroundColor: color.surface,
    borderRadius: radius.lg,
    borderWidth: 1,
    borderColor: color.border,
  },
  
  voteButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.xs,
    paddingVertical: space.sm,
    paddingHorizontal: space.md,
    borderRadius: radius.md,
    backgroundColor: color.backgroundSecondary,
    minWidth: 70,
    justifyContent: 'center',
  },
  voteButtonActive: {
    // Base active style - specific colors applied via upvoteActive/downvoteActive
  },
  upvoteActive: {
    backgroundColor: color.success,
  },
  downvoteActive: {
    backgroundColor: color.error,
  },
  voteButtonPressed: {
    opacity: 0.8,
  },
  
  voteCount: {
    fontSize: text.body.fontSize,
    fontWeight: '600',
    color: color.textPrimary,
  },
  voteCountActive: {
    color: color.surface,
  },
  
  scoreContainer: {
    alignItems: 'center',
    minWidth: 60,
  },
  score: {
    fontSize: text.pageTitle.fontSize,
    fontWeight: text.pageTitle.fontWeight,
    color: color.textPrimary,
  },
  scorePositive: {
    color: color.success,
  },
  scoreNegative: {
    color: color.error,
  },
  scoreLabel: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginTop: 2,
  },
});

